import { MainView } from '@repo/ui/Views';
import { useUser } from '@repo/hooks/useUser';
import { POLICIES, Authorization } from '@/lib/Authorization';
import { FallbackCard } from '@repo/ui/Card';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client/react';

import UpdateApiKeyForm from '../components/UpdateApiKeyForm';
import DeleteApiKey from '../components/DeleteApiKey';
import ApiKeyTabNav, { apiKeyTabPath, type ApiKeyTab } from '../components/ApiKeyTabNav';
import API_KEYS_SHOW_Q from '../gql/api_keys/show.gql';
import DidsForResourceTable from '@/features/dids/components/DidsForResourceTable';
import { useHasDids } from '@/features/dids/hooks/useHasDids';

interface UpdateApiKeyProps {
  tab?: ApiKeyTab;
}

const UpdateApiKey = ({ tab = 'overview' }: UpdateApiKeyProps) => {
  const user = useUser();
  const { accountId, apiKeyId } = useParams();

  const { loading, data, refetch } = useQuery(API_KEYS_SHOW_Q, {
    variables: {
      id: String(apiKeyId),
    },
  });

  // Only the Overview tab (delete) needs to know whether numbers are attached
  const { hasDids, loading: checkingDids } = useHasDids(
    'apiKey',
    tab === 'overview' ? apiKeyId : undefined
  );

  const apiKey = data?.apiKey;

  return (
    <MainView title={loading ? 'Update API Key' : `Update API Key: ${apiKey?.name}`}>
      <ApiKeyTabNav />
      <Authorization
        policyCheck={POLICIES['api_keys:update'](user)}
        fallbackView={
          <FallbackCard
            title="No Access"
            description="You do not have the permissions to update API keys."
          />
        }
      >
        {apiKey && accountId ? (
          tab === 'numbers' ? (
            POLICIES['dids:index'](user) ? (
              <DidsForResourceTable
                resource="apiKey"
                resourceId={apiKey.id}
                accountId={accountId}
              />
            ) : (
              <FallbackCard description="You do not have permissions to view numbers." />
            )
          ) : (
            <div className="flex flex-col gap-6">
              <UpdateApiKeyForm
                apiKeyId={apiKey.id}
                name={apiKey.name}
                notes={apiKey.notes}
                refetch={refetch}
              />
              {POLICIES['api_keys:delete'](user) && (
                <DeleteApiKey
                  apiKeyId={apiKey.id}
                  name={apiKey.name}
                  loading={loading}
                  accountId={accountId}
                  hasDids={hasDids}
                  checkingDids={checkingDids}
                  numbersHref={apiKeyTabPath(accountId, apiKey.id, 'numbers')}
                />
              )}
            </div>
          )
        ) : null}
      </Authorization>
    </MainView>
  );
};

export default UpdateApiKey;
